import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  Calendar,
  Clock,
  Video,
  ExternalLink,
  History,
  PlayCircle,
} from "lucide-react";

/* ---------- TYPES ---------- */

interface Training {
  id: string;
  type: "upcoming" | "past";
  title: string;
  platform: string | null;
  date: string;
  time: string;
  meeting_link: string | null;
  recording_link: string | null;
  password: string | null;
  trainer_name: string;
  additional_info: string | null;
  description: string | null;
}

/* ---------- COMPONENT ---------- */

export function TrainingTab() {
  const [allUpcoming, setAllUpcoming] = useState<Training[]>([]);
  const [upcoming, setUpcoming] = useState<Training[]>([]);
  const [past, setPast] = useState<Training[]>([]);
  const [topSession, setTopSession] = useState<Training | null>(null);
  const [loading, setLoading] = useState(true);

  /* ---------- INITIAL LOAD (ONLY ONCE) ---------- */

  useEffect(() => {
    loadTrainings();
  }, []);

  /* ---------- SILENT ENGINE ---------- */

  useEffect(() => {
    if (!allUpcoming.length) {
      setTopSession(null);
      return;
    }

    function calculateTopSession() {
      const now = new Date();

      const sorted = [...allUpcoming].sort(
        (a, b) =>
          new Date(a.date + "T" + a.time).getTime() -
          new Date(b.date + "T" + b.time).getTime()
      );

      let nextValid: Training | null = null;

      for (const session of sorted) {
        const sessionStart = new Date(`${session.date}T${session.time}`);
        const sessionEnd = new Date(
          sessionStart.getTime() + 2 * 60 * 60 * 1000
        );

        if (now <= sessionEnd) {
          nextValid = session;
          break;
        }
      }

      setTopSession(nextValid);
    }

    calculateTopSession();

    const interval = setInterval(calculateTopSession, 60000);

    return () => clearInterval(interval);
  }, [allUpcoming]);

  /* ---------- LOAD FROM DATABASE ---------- */

  async function loadTrainings() {
    setLoading(true);

    const { data: upcomingData } = await supabase
      .from("trainings")
      .select("*")
      .eq("type", "upcoming")
      .order("date", { ascending: true })
      .order("time", { ascending: true });

    const { data: pastData } = await supabase
      .from("trainings")
      .select("*")
      .eq("type", "past")
      .order("date", { ascending: false })
      .order("time", { ascending: false });

    const now = new Date();
    const futureOnly: Training[] = [];

    for (const session of upcomingData || []) {
      const sessionStart = new Date(`${session.date}T${session.time}`);
      if (sessionStart > now) {
        futureOnly.push(session);
      }
    }

    setAllUpcoming(upcomingData || []);
    setUpcoming(futureOnly);
    setPast(pastData || []);
    setLoading(false);
  }

  if (loading)
    return (
      <div className="text-center py-12 text-gray-500">
        Loading training...
      </div>
    );

  return (
    <div className="space-y-8">

      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Training Sessions
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          View upcoming and past training sessions
        </p>
      </div>

      {/* SECTION 1 — SILENT ENGINE CARD */}
      {topSession ? (
        <div className="bg-nhs-blue rounded-xl shadow-lg p-6 text-white">
          {(() => {
            const now = new Date();
            const sessionStart = new Date(
              `${topSession.date}T${topSession.time}`
            );
            const sessionEnd = new Date(
              sessionStart.getTime() + 2 * 60 * 60 * 1000
            );

            const isLive =
              now >= sessionStart && now <= sessionEnd;

            return (
              <h2 className="text-lg font-semibold mb-2 text-blue-100">
                {isLive
                  ? "🟢 NOW IN SESSION"
                  : "🟡 Next Training"}
              </h2>
            );
          })()}

          <div className="text-2xl font-bold mb-4">
            {topSession.title}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-blue-200 text-sm">Date</p>
              <p className="font-semibold">{topSession.date}</p>
            </div>

            <div>
              <p className="text-blue-200 text-sm">Time</p>
              <p className="font-semibold">{topSession.time}</p>
            </div>

            <div>
              <p className="text-blue-200 text-sm">Platform</p>
              <p className="font-semibold">
                {topSession.platform}
              </p>
            </div>
            <div>
              <p className="text-blue-200 text-sm">Trainer</p>
              <p className="font-semibold">
                {topSession.trainer_name}
              </p>
            </div>
            {topSession.additional_info && (
              <p className="mt-4 text-blue-100">
                {topSession.additional_info}
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            {topSession.meeting_link && (
              <a
                href={topSession.meeting_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-nhs-blue px-6 py-2 rounded-lg font-bold hover:bg-gray-100"
              >
                <Video className="h-4 w-4" />
                Join Session
              </a>
            )}

            {topSession.password && (
              <span className="text-sm text-blue-200">
                Password: {topSession.password}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-nhs-blue rounded-xl p-6 text-center text-white">
          🔴 No Available Training
        </div>
      )}

      {/* SECTION 2 — UPCOMING LIST */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Upcoming Sessions
        </h3>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {upcoming.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {upcoming.map((session) => (
                <div key={session.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.title}
                      </p>

                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {session.date}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {session.time}
                        </div>
                        <div className="flex items-center gap-1">
                          <Video className="h-3 w-3" />
                          {session.platform}
                        </div>
                      </div>
                    </div>

                    {session.meeting_link && (
                      <a
                        href={session.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-nhs-blue"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-nhs-blue rounded-xl p-6 text-center text-white">
              🔴 No Available Training
            </div>
          )}
        </div>
      </div>

      {/* PAST SECTION (UNCHANGED) */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <History className="h-5 w-5" />
          Past Trainings
        </h3>

        {past.length > 0 ? (
          <div className="grid gap-4">
            {past.map((training) => (
              <div
                key={training.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {training.title}
                    </h4>

                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <Calendar className="h-3 w-3" />
                      {training.date}
                    </div>

                    {training.description && (
                      <p className="text-sm text-gray-600 mt-2">
                        {training.description}
                      </p>
                    )}

                    {training.password && (
                      <p className="text-sm text-gray-500 mt-1">
                        Password: {training.password}
                      </p>
                    )}
                  </div>

                  {training.recording_link && (
                    <a
                      href={training.recording_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-nhs-blue text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-nhs-dark ml-4"
                    >
                      <PlayCircle className="h-4 w-4" />
                      Watch
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            No past trainings available yet.
          </div>
        )}
      </div>
    </div>
  );
}